import {useEffect, useMemo, useState, useLayoutEffect} from 'react'
import {Button, Form, InputGroup, Modal} from 'react-bootstrap'
import {useDisclourse} from '../../hooks/useDisclourse'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_CONTACTS_PREVIEW} from '../../gql/queries/contactsApiQueries'
import {ModalBodySearchContactHeader} from './ModalBodySearchContactHeader'
import {toast} from 'react-toastify'
import {IResContactsPreviewAPI} from '../../types/contactsApi'
import {useIntl} from 'react-intl'
import {SEARCH_MESSAGES as S} from './searchMessages'
// Inline stylesheet, not translatable content — hoisted out of the JSX so the
// i18n rule does not read the CSS as untranslated text.
const WIDE_MODAL_CSS = `
  .modal.show .contact-search-modal-wide,
  .contact-search-modal-wide.modal-dialog {
    max-width: 95vw !important;
    width: 95vw !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  .contact-search-modal-wide .modal-content {
    width: 100% !important;
  }
  .contact-search-modal-wide .modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
`

export const SearchContactHeader = () => {
  const intl = useIntl()
  const [gqlGet, {data, loading, error}] = useLazyQuery<IResContactsPreviewAPI>(GET_ALL_CONTACTS_PREVIEW, {
    fetchPolicy: 'no-cache',
  })

  const {isOpen, onClose, onOpen} = useDisclourse()
  const [searchInput, setSearchInput] = useState('')
  const [customLoading, setCustomLoading] = useState(false)
  const [mergedData, setMergedData] = useState<IResContactsPreviewAPI | undefined>(undefined)

  // Garantiza que el modal se abra siempre ancho
  useLayoutEffect(() => {
    if (!isOpen) return
    const applyWidth = () => {
      const dialog = document.querySelector('.contact-search-modal-wide .modal-dialog') as HTMLElement | null
      if (dialog) {
        dialog.style.maxWidth = '95vw'
        dialog.style.width = '95vw'
      }
    }

    // Aplicar inmediatamente
    applyWidth()

    // Aplicar múltiples veces para asegurar que tome efecto
    const timeouts = [0, 10, 50, 100, 150].map(delay =>
      window.setTimeout(applyWidth, delay)
    )

    const handleResize = () => applyWidth()
    window.addEventListener('resize', handleResize)

    return () => {
      timeouts.forEach(id => window.clearTimeout(id))
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(S.errorWithMessage, {message: error.message}))
  }, [error])

  const searchContacts = async (str: string) => {
    const raw = str.trim()
    if (raw.length < 3) return

    setCustomLoading(true)
    try {
      // El backend ahora maneja la tokenización correctamente con AND
      // Solo necesitamos enviar una query con el string completo
      const {data: resp} = await gqlGet({
        variables: {name: raw},
        fetchPolicy: 'no-cache',
      })

      setMergedData(resp)
    } catch (err: any) {
      toast.error(err?.message || 'Error searching contacts')
    } finally {
      setCustomLoading(false)
    }
  }

  return (
    <>
      <div className='menu-item me-lg-1 w-100'>
        <form
          style={{width: '90%', marginTop: '10px'}}
          autoComplete='off'
          onSubmit={(e) => {
            e.preventDefault()
            const {search} = Object.fromEntries(new FormData(e.currentTarget))
            onOpen()
            searchContacts(search.toString())
          }}
        >
          <InputGroup className='mb-3'>
            <Form.Control
              aria-label={intl.formatMessage(S.ariaSearchContacts)}
              aria-describedby='basic-addon1'
              name='search'
              type='search'
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                if (!e.target.value) return
                onOpen()
              }}
              placeholder={intl.formatMessage(S.contactPlaceholder)}
            />
            <Button variant='secondary' type='submit' id='button-addon1'>
              <i className='bi bi-search text-dark' />
            </Button>
          </InputGroup>
        </form>
      </div>

      <Modal show={isOpen} onHide={onClose} dialogClassName='contact-search-modal-wide'>
        <style>{WIDE_MODAL_CSS}</style>
        <Modal.Header closeButton>
          <form
            autoComplete='off'
            className='w-100'
            onSubmit={(e) => {
              e.preventDefault()
              const {search} = Object.fromEntries(new FormData(e.currentTarget))
              searchContacts(search.toString())
            }}
          >
            <InputGroup className='w-100' size='sm'>
              <Button variant='transparent' type='submit' id='button-addon1'>
                <i className='bi bi-search text-info fs-2 ' />
              </Button>
              <Form.Control
                autoFocus
                aria-label={intl.formatMessage(S.ariaSearchContacts)}
                aria-describedby='basic-addon1'
                name='search'
                className='border-0'
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  if (e.target.value === '') onClose()
                }}
                placeholder={intl.formatMessage(S.contactPlaceholderLong)}
                minLength={3}
              />
            </InputGroup>
          </form>
        </Modal.Header>
        <ModalBodySearchContactHeader
          data={mergedData || data}
          loading={customLoading || loading}
          onClose={onClose}
          keyword={searchInput}
        />
      </Modal>
    </>
  )
}
